import React from 'react';
import { graphql, Link } from 'gatsby';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { FaCalendar, FaTags, FaShare, FaFacebookF, FaTwitter, FaLinkedinIn } from 'react-icons/fa';
import './blog-post.css';

const BlogPostTemplate = ({ data }) => {
  const post = data.markdownRemark;
  const { previous, next } = data;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const childVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.frontmatter.title,
    "datePublished": post.frontmatter.date,
    "dateModified": post.frontmatter.date,
    "author": {
      "@type": "Organization",
      "name": "ResumeFlex"
    },
    "image": post.frontmatter.featuredImage,
    "description": post.frontmatter.description,
    "keywords": post.frontmatter.categories.join(", "),
    "publisher": {
      "@type": "Organization",
      "name": "ResumeFlex",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.resumeflex.com/images/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.resumeflex.com/blog/${post.fields.slug}`
    }
  };

  const addUTMParams = (content) => {
    const utmParams = `utm_source=blog&utm_medium=post&utm_campaign=${encodeURIComponent(post.frontmatter.title)}`;
    return content.replace(
      /(https:\/\/app\.resumeflex\.com(?:\/[^"')\s]*)?)/g,
      (match) => `${match}${match.includes('?') ? '&' : '?'}${utmParams}`
    );
  };
  
  const postContentWithUTM = addUTMParams(post.html);

  return (
    <Layout>
      <motion.div
        className="bg-gradient-to-b from-gray-50 to-white min-h-screen py-16 px-4 sm:px-6 lg:px-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-4xl mx-auto" style={{ marginTop: '15vh' }}>
          <motion.header className="mb-12 text-center" variants={childVariants}>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
              {post.frontmatter.title}
            </h1>
            <div className="flex items-center justify-center text-gray-600 text-sm mb-4">
              <FaCalendar className="mr-2" />
              <time dateTime={post.frontmatter.date}>{post.frontmatter.date}</time>
            </div>
            <div className="flex flex-wrap items-center justify-center mb-6">
              <FaTags className="text-gray-400 mr-2" />
              {post.frontmatter.categories.map((category, index) => (
                <span
                  key={index}
                  className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full mr-2 mb-2"
                >
                  {category}
                </span>
              ))}
            </div>
          </motion.header>

          {post.frontmatter.featuredImage && (
            <motion.div className="mb-12" variants={childVariants}>
              <img
                src={post.frontmatter.featuredImage}
                alt={post.frontmatter.title}
                className="w-full h-auto object-cover rounded-xl shadow-lg"
              />
            </motion.div>
          )}

          <motion.article
            className="prose prose-lg mx-auto mb-12"
            variants={childVariants}
            dangerouslySetInnerHTML={{ __html: postContentWithUTM }}
          />

          <motion.div
            className="border-t border-b border-gray-200 py-12 mb-12"
            variants={childVariants}
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <FaShare className="mr-2" /> Share this post
            </h3>
            <div className="flex space-x-4">
              <a href={`https://www.facebook.com/sharer/sharer.php?u=https://www.resumeflex.com/blog/${post.fields.slug}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors duration-300">
                <FaFacebookF size={24} />
              </a>
              <a href={`https://twitter.com/intent/tweet?url=https://www.resumeflex.com/blog/${post.fields.slug}&text=${encodeURIComponent(post.frontmatter.title)}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-600 transition-colors duration-300">
                <FaTwitter size={24} />
              </a>
              <a href={`https://www.linkedin.com/shareArticle?mini=true&url=https://www.resumeflex.com/blog/${post.fields.slug}&title=${encodeURIComponent(post.frontmatter.title)}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:text-blue-900 transition-colors duration-300">
                <FaLinkedinIn size={24} />
              </a>
            </div>
          </motion.div>

          <nav className="flex flex-wrap justify-between mb-12">
            {previous && (
              <Link
                to={previous.fields.slug}
                className="bg-white shadow-md rounded-lg p-6 w-full md:w-[48%] mb-4 md:mb-0 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                <span className="text-sm text-gray-500 block mb-2">Previous Post</span>
                <span className="text-xl font-semibold text-gray-900">{previous.frontmatter.title}</span>
              </Link>
            )}
            {next && (
              <Link
                to={next.fields.slug}
                className="bg-white shadow-md rounded-lg p-6 w-full md:w-[48%] hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                <span className="text-sm text-gray-500 block mb-2">Next Post</span>
                <span className="text-xl font-semibold text-gray-900">{next.frontmatter.title}</span>
              </Link>
            )}
          </nav>
        </div>
      </motion.div>
    </Layout>
  );
};

export const Head = ({ data }) => {
  const post = data.markdownRemark;
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.frontmatter.title,
    "datePublished": post.frontmatter.date,
    "dateModified": post.frontmatter.date,
    "author": {
      "@type": "Organization",
      "name": "ResumeFlex"
    },
    "image": post.frontmatter.featuredImage,
    "description": post.frontmatter.description,
    "keywords": post.frontmatter.categories.join(", "),
    "publisher": {
      "@type": "Organization",
      "name": "ResumeFlex",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.resumeflex.com/images/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.resumeflex.com/blog/${post.fields.slug}`
    }
  };

  return (
    <>
      <title>{`${post.frontmatter.title} | ResumeFlex Blog`}</title>
      <meta name="description" content={post.frontmatter.description || post.excerpt} />
      <meta name="keywords" content={post.frontmatter.categories.join(", ")} />

      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={post.frontmatter.title} />
      <meta property="og:description" content={post.frontmatter.description || post.excerpt} />
      <meta property="og:image" content={post.frontmatter.featuredImage} />
      <meta property="og:url" content={`https://www.resumeflex.com/blog/${post.fields.slug}`} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={post.frontmatter.title} />
      <meta name="twitter:description" content={post.frontmatter.description || post.excerpt} />
      <meta name="twitter:image" content={post.frontmatter.featuredImage} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData, null, 2)}
      </script>
    </>
  );
};

export const query = graphql`
  query($slug: String!, $previousPostId: String, $nextPostId: String) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      excerpt(pruneLength: 160)
      fields {
        slug
      }
      frontmatter {
        title
        date(formatString: "YYYY-MM-DD")
        categories
        description
        featuredImage
      }
    }
    previous: markdownRemark(id: { eq: $previousPostId }) {
      fields {
        slug
      }
      frontmatter {
        title
      }
    }
    next: markdownRemark(id: { eq: $nextPostId }) {
      fields {
        slug
      }
      frontmatter {
        title
      }
    }
  }
`;

export default BlogPostTemplate;